import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Maximize2, Minimize2, Volume2, VolumeX } from "lucide-react";
import { useAction, useMutation, useQuery } from "convex/react";
import { motion } from "motion/react";

import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { ChatResizeHandle } from "@/components/chat/ChatResizeHandle";
import { ChatWindow } from "@/components/chat/ChatWindow";
import { SkipOnboardingButton } from "@/components/chat/SkipOnboardingButton";
import { ThreadSidebar } from "@/components/chat/ThreadSidebar";
import { InboxPeek } from "@/components/inbox/InboxPeek";
import { StudioWorldWindow } from "@/components/apps/StudioWorldWindow";
import { FileManagerContent } from "@/components/dashboard/FileManagerContent";
import { TelephoneIntegrationPanel } from "@/components/dashboard/TelephoneIntegrationPanel";
import { CommandViewport } from "@/components/engine/CommandViewport";
import { PropTooltip } from "@/components/engine/InteractiveProp";
import { NotebookQuickView } from "@/components/notebook/NotebookQuickView";
import { TaskPanel } from "@/components/tasks/TaskPanel";
import { TasksPeek } from "@/components/tasks/TasksPeek";
import { FloatingWindow } from "@/components/ui/FloatingWindow";
import { useAuthToken } from "@convex-dev/auth/react";
import { useAgentSync } from "@/hooks/useAgentSync";
import { useMobilePanelCoordination } from "@/hooks/useMobilePanelCoordination";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { useUser } from "@/hooks/useUser";
import type { WsState } from "@/lib/wsManager";
import { usePodStatus } from "@/hooks/usePodStatus";
import { useChatPanelStore } from "@/store/chatPanelStore";
import { useEngineStore } from "@/store/useEngineStore";
import { useSidebarStore } from "@/store/sidebarStore";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/chat/$threadId")({
  component: ThreadView,
  validateSearch: (
    search: Record<string, unknown>,
  ): { seed?: string } => {
    // Optional composer prefill (e.g. the workflows library's "Describe a new
    // job"). Pre-fills the chat input ONCE for the user to review + send
    // manually — never auto-sent. Absent → chat behaves exactly as before, and
    // the key is OMITTED (not `undefined`) so existing navigations to this
    // route need not supply a `search`.
    return typeof search.seed === "string" ? { seed: search.seed } : {};
  },
});

const MemoizedViewport = React.memo(CommandViewport);

function ThreadView() {
  const { threadId } = Route.useParams();
  const { seed } = Route.useSearch();
  const authToken = useAuthToken();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const { podState, isRunning, isProvisioning } = usePodStatus();
  const [wsState, setWsState] = useState<WsState>("disconnected");
  const onWsStateChange = useCallback((state: WsState) => setWsState(state), []);
  const { width, setWidth, boardHidden, setBoardHidden, soundsMuted, toggleSoundsMuted } =
    useChatPanelStore();
  const sidebarMode = useSidebarStore((s) => s.mode);
  useMobilePanelCoordination();

  const agentStatusLabel = isProvisioning ? (podState === "starting" ? "Starting" : "Setting up")
    : podState === "error" ? "Error"
    : podState === "scaled_down" ? "Paused"
    : wsState === "connected" ? "Running"
    : wsState === "connecting" || wsState === "disconnected" ? "Connecting"
    : wsState === "error" ? "Disconnected"
    : (podState ?? "Unknown");

  // Initialize the 3D agent on mount
  useAgentSync();

  // On mobile, tapping the lobster shows the chat panel
  useEffect(() => {
    function onAgentTapped() {
      setBoardHidden(true);
    }
    window.addEventListener("agent-tapped", onAgentTapped);
    return () => window.removeEventListener("agent-tapped", onAgentTapped);
  }, [setBoardHidden]);

  // Prop popup data
  const activePropIds = useEngineStore((s) => s.activePropIds);
  const deactivateProp = useEngineStore((s) => s.deactivateProp);
  const navigate = useNavigate();

  // Strip the one-shot `seed` param once ChatWindow has captured it into its
  // draft state, so reload/back-forward doesn't re-prefill an already-consumed
  // seed (same consume-then-strip discipline as the dashboard `?checkout=`
  // param, frontend-doctrine §4.11).
  useEffect(() => {
    if (seed === undefined) return;
    void navigate({
      to: "/chat/$threadId",
      params: { threadId },
      search: {},
      replace: true,
    });
  }, [seed, threadId, navigate]);
  // "New note created" card → the full-page notebook with that exact note open
  // (§4.17). Navigation is the ROUTE's job (§4.6); the chat components below
  // only receive the callback.
  const onOpenNote = useCallback(
    (path: string) => {
      void navigate({ to: "/notebook", search: { path } });
    },
    [navigate],
  );

  const { user } = useUser();
  // Starter-chip gate (onboarding-v2 §3.6): hide the chips until the backend
  // confirms onboarding is done. Loading and unauthenticated both read as
  // not-onboarded, so the chips never flash under the agent's opening message.
  // `mode` is the SEPARATE tri-state the locks ride (frontend-doctrine §4.16) —
  // the chip gate must stay fail-closed, the locks must not.
  const { onboardingComplete, mode: onboardingMode } = useOnboardingStatus();
  const skipOnboarding = useMutation(api.onboarding.skipOnboarding);
  const makeEmailAddress = useMutation(api.emails.makeEmailAddress);

  // File manager state
  const convexSiteUrl = (import.meta.env.VITE_CONVEX_URL as string | undefined)?.replace(".cloud", ".site") ?? "";
  const buildFileUrl = useCallback((gcsPath: string) => `${convexSiteUrl}/api/file-url?path=${encodeURIComponent(gcsPath)}`, [convexSiteUrl]);

  const rawUserFiles = useQuery(api.userFiles.list);
  const linqNumber = useQuery(api.linqNumbers.getMyLinqNumber, {});
  const userFiles = useMemo(() => (rawUserFiles ?? []).map((f) => ({ ...f, url: f.gcsPath ? buildFileUrl(f.gcsPath) : undefined })), [rawUserFiles, buildFileUrl]);
  const generateFileUploadUrl = useAction(api.gcsActions.generateUploadUrl);
  const saveFileMut = useMutation(api.userFiles.saveFile);
  const renameFileMut = useMutation(api.userFiles.renameFile);
  const deleteFileMut = useMutation(api.userFiles.deleteFile);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);

  const handleFileUpload = useCallback(async (file: File) => {
    setIsFileUploading(true);
    setFileUploadError(null);
    try {
      const { uploadUrl, gcsPath } = await generateFileUploadUrl({ flow: "user-storage" as const, filename: file.name, mimeType: file.type || "application/octet-stream" });
      const res = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!res.ok) throw new Error(`Upload failed (HTTP ${res.status})`);
      await saveFileMut({ gcsPath, filename: file.name, mimeType: file.type || "application/octet-stream", size: file.size });
    } catch (err) {
      setFileUploadError(err instanceof Error ? err.message : "Upload failed. Try again.");
    } finally {
      setIsFileUploading(false);
    }
  }, [generateFileUploadUrl, saveFileMut]);

  return (
    <div className={cn("fixed inset-0", sidebarMode === "rail" ? "lg:left-14" : "lg:left-52")}>
      {/* Canvas — always renders when board is visible (mobile + desktop).
          On desktop it occupies only the right region (starting at the chat's
          edge) so the island centers in its own panel instead of hiding half
          behind the chat. On mobile it's full (binary full-board / full-chat). */}
      {!boardHidden && (
        <div
          className="absolute inset-0 select-none lg:left-[var(--board-left)]"
          style={{ WebkitTouchCallout: "none", "--board-left": `${width}px` } as React.CSSProperties}
        >
          <MemoizedViewport />
        </div>
      )}

      {/* Mobile chat/world switching lives in the bottom bar (TopBar). */}

      {/* Everything else zooms in */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="pointer-events-none absolute inset-0 origin-center"
      >
        {/* HUD */}
        {!boardHidden && (
          <div className="pointer-events-none absolute inset-0 z-40">
            <PropTooltip />
            {activePropIds.includes("mailbox") && (
              <FloatingWindow title="Inbox" onClose={() => deactivateProp({ propId: "mailbox" })} defaultSize={{ width: 400, height: 480 }} minSize={{ width: 340, height: 380 }}>
                {/* Door: peek at recent mail → open the full /inbox page. */}
                <InboxPeek
                  agentEmailAddress={user?.agentEmailAddress}
                  onCreateEmailAddress={async () => { await makeEmailAddress({}); }}
                  onOpenInbox={() => {
                    deactivateProp({ propId: "mailbox" });
                    void navigate({ to: "/inbox" });
                  }}
                />
              </FloatingWindow>
            )}
            {activePropIds.includes("computer") && (
              <FloatingWindow
                title="Studio"
                onClose={() => deactivateProp({ propId: "computer" })}
                defaultSize={{ width: 460, height: 560 }}
                minSize={{ width: 360, height: 440 }}
              >
                {/* Studio floats OVER the world by default: browse apps and
                    open live previews here, with "Open full Studio" as the
                    escape hatch. NOTE: this window replaced the
                    IntegrationsPeek that lived on the computer — Integrations
                    needs a new home (another prop or settings); flagged, not
                    resolved here. */}
                <StudioWorldWindow
                  onOpenStudio={() => {
                    deactivateProp({ propId: "computer" });
                    void navigate({ to: "/studio" });
                  }}
                />
              </FloatingWindow>
            )}
            {activePropIds.includes("telephone") && (
              <FloatingWindow title="Telephone" onClose={() => deactivateProp({ propId: "telephone" })}>
                <TelephoneIntegrationPanel linqNumber={linqNumber} />
              </FloatingWindow>
            )}
            {activePropIds.includes("notebook") && (
              <FloatingWindow
                title="Notebook"
                onClose={() => deactivateProp({ propId: "notebook" })}
                defaultSize={{ width: 520, height: 620 }}
                minSize={{ width: 380, height: 420 }}
              >
                {/* Quick door — peek and search here; anything deeper
                    promotes to the full-page workspace at /notebook. */}
                <NotebookQuickView
                  onOpenPage={(path) => {
                    deactivateProp({ propId: "notebook" });
                    void navigate({ to: "/notebook", search: path ? { path } : {} });
                  }}
                />
              </FloatingWindow>
            )}
            {activePropIds.includes("fileCabinet") && (
              <FloatingWindow title="Files" onClose={() => deactivateProp({ propId: "fileCabinet" })}>
                <FileManagerContent
                  files={userFiles}
                  isUploading={isFileUploading}
                  uploadError={fileUploadError}
                  onUpload={handleFileUpload}
                  onRename={async (fileId, newName) => { await renameFileMut({ fileId: fileId as Id<"user_files">, filename: newName }); }}
                  onDelete={async (fileId) => { await deleteFileMut({ fileId: fileId as Id<"user_files"> }); }}
                />
              </FloatingWindow>
            )}
            {activePropIds.includes("calendar") && (
              <FloatingWindow title="Task Manager" onClose={() => deactivateProp({ propId: "calendar" })} defaultSize={{ width: 400, height: 480 }} minSize={{ width: 340, height: 380 }}>
                {/* Door: peek at what's on the plate → open the full /tasks page. */}
                <TasksPeek
                  onOpenTasks={() => {
                    deactivateProp({ propId: "calendar" });
                    void navigate({ to: "/tasks" });
                  }}
                />
              </FloatingWindow>
            )}
          </div>
        )}

        {/* Chat Panel — overlays on top of 3D world, or full width when board hidden */}
        {/* On mobile: binary — full chat OR full 3D, never both */}
        <>
            <div
              style={boardHidden ? { right: 0 } : { width }}
              className={cn(
                "pointer-events-auto z-20 flex min-h-0 flex-col",
                // Reserve room for the fixed mobile bottom bar so the composer clears it,
                // and pad the top so the mini-navbar clears the notch on full-screen
                // mobile chat (viewport-fit=cover paints under it; the panel bg fills it).
                "max-lg:pt-[env(safe-area-inset-top)] max-lg:pb-[calc(3.5rem+env(safe-area-inset-bottom))]",
                boardHidden
                  ? "absolute inset-y-0 left-0 bg-white dark:bg-surface-dark"
                  : "absolute inset-y-0 left-0 border-r border-black/5 bg-home-cream dark:border-white/10 dark:bg-surface-dark max-lg:hidden",
              )}
            >
              {/* Mini navbar */}
              <div
                className={cn(
                  "flex shrink-0 items-center justify-between border-b border-black/5 px-5 py-4 dark:border-white/10",
                  boardHidden && "lg:pr-12",
                )}
              >
                <div className="flex items-center gap-3">
                  <img
                    src="/images/clawbert-chat-avatar.png"
                    alt="Clawbert"
                    className="h-8 w-8 shrink-0 rounded-lg object-cover"
                  />
                  <div>
                    <h3 className="font-heading text-sm font-bold text-black dark:text-white">Clawbert</h3>
                    <div className="flex items-center gap-1.5">
                      <div className={cn(
                        "h-1.5 w-1.5 rounded-full",
                        isRunning && wsState === "connected" ? "bg-accent-green"
                          : isProvisioning || wsState === "connecting" || wsState === "disconnected" ? "bg-accent-yellow animate-pulse"
                          : podState === "error" || wsState === "error" ? "bg-brand-coral"
                          : "bg-black/30 dark:bg-white/30",
                      )} />
                      <span className="text-xs font-medium text-black/50 dark:text-white/50">
                        {agentStatusLabel}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {/* One-way escape hatch out of the first-run ritual. Renders
                      nothing outside `mode === "onboarding"`; the component
                      owns that rule and its own confirm dialog. */}
                  <SkipOnboardingButton
                    mode={onboardingMode}
                    onSkip={() => {
                      void skipOnboarding({}).catch(() => {});
                    }}
                  />
                  <button
                    onClick={toggleSoundsMuted}
                    title={soundsMuted ? "Unmute sounds" : "Mute sounds"}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-black/70 transition-colors hover:bg-black/5 dark:border-white/15 dark:text-white/70 dark:hover:bg-white/5"
                  >
                    {soundsMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                    <span className="sr-only">
                      {soundsMuted ? "Unmute sounds" : "Mute sounds"}
                    </span>
                  </button>
                  <button
                    onClick={() => setBoardHidden(!boardHidden)}
                    title={boardHidden ? "Show board" : "Hide board"}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-black/70 transition-colors hover:bg-black/5 max-lg:hidden dark:border-white/15 dark:text-white/70 dark:hover:bg-white/5"
                  >
                    {boardHidden ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                    <span className="sr-only">
                      {boardHidden ? "Show board" : "Hide board"}
                    </span>
                  </button>
                </div>
              </div>

              <ChatWindow
                className="flex-1 overflow-hidden"
                threadId={threadId as Id<"threads">}
                authToken={authToken}
                onWsStateChange={onWsStateChange}
                initialDraft={seed}
                starterSet={onboardingComplete ? undefined : null}
                awaitingKickoff={onboardingMode === "onboarding"}
                onOpenNote={onOpenNote}
              />
            </div>
            {!boardHidden && (
              <div className="pointer-events-auto absolute inset-y-0 z-30 hidden lg:flex" style={{ left: width }}>
                <ChatResizeHandle onResize={setWidth} />
              </div>
            )}
          </>

        {/* The per-thread Chat collapse tab and the workflow re-entry tab were
            removed. The expand icon toggles full-screen chat; the resize
            handle adjusts the split. Mobile chat/world switching is in the
            bottom bar. */}

        {/* Right-side task panel — slide-out, paired with the chat panel */}
        <TaskPanel threadId={threadId as Id<"threads">} />

        {/* z-30: Thread sidebar overlays everything when open */}
        {sidebarOpen && (
          <div className="pointer-events-auto fixed inset-y-0 left-0 z-30 w-[280px]">
            <ThreadSidebar activeThreadId={threadId} onClose={() => setSidebarOpen(false)} />
          </div>
        )}
      </motion.div>
    </div>
  );
}
